/* =============================================================================
   admin-editors.jsx  —  host-facing editors for settings + content.
   Operate on AdminApp's `settings` state via setSection(key, value).
   Persistence happens on the explicit Save in AdminApp.
============================================================================= */
import { useState, useEffect } from 'react';
import { Field, Input, TextArea, Toggle, useAsyncData, Loading, ErrorBlock } from './ui';
import { DB, makeId } from '../lib/api';

/* small reusable add/remove list scaffold */
function ListRow({ children, onRemove, onUp, onDown }) {
  return (
    <div className="list-item">
      <div className="grow">{children}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {onUp && <button className="icon-btn" title="Move up" onClick={onUp}>&uarr;</button>}
        {onDown && <button className="icon-btn" title="Move down" onClick={onDown}>&darr;</button>}
        {onRemove && <button className="icon-btn" title="Remove" onClick={onRemove}>&times;</button>}
      </div>
    </div>
  );
}
function reorder(arr, i, dir) {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return arr;
  const next = arr.slice();
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

/* ----------------------------- event settings ---------------------------- */
export function SettingsEditor({ settings, setSection }) {
  const e = settings.event;
  const up = (k) => (v) => setSection('event', { ...e, [k]: v });
  return (
    <div className="admin-card">
      <h3>Event details</h3>
      <p className="muted" style={{ marginTop: 0 }}>These appear across the invitation.</p>
      <div className="grid-2">
        <Field label="Headline"><Input value={e.headline} onChange={up('headline')} /></Field>
        <Field label="Hosts' names" hint="Shown in the nav and footer."><Input value={e.hosts} onChange={up('hosts')} /></Field>
      </div>
      <Field label="Subline"><Input value={e.subline} onChange={up('subline')} /></Field>
      <div className="grid-3">
        <Field label="Date (display)" hint="As guests read it"><Input value={e.dateDisplay} onChange={up('dateDisplay')} /></Field>
        <Field label="Date (for calendar)" hint="YYYY-MM-DD"><Input type="date" value={e.dateISO} onChange={up('dateISO')} /></Field>
        <Field label="Start time"><Input value={e.time} onChange={up('time')} placeholder="11:00 AM" /></Field>
      </div>
      <div className="grid-3">
        <Field label="End time"><Input value={e.endTime} onChange={up('endTime')} placeholder="2:00 PM" /></Field>
        <Field label="Venue name"><Input value={e.venueName} onChange={up('venueName')} placeholder="(left blank for host)" /></Field>
        <Field label="Venue address"><Input value={e.venueAddress} onChange={up('venueAddress')} placeholder="(left blank for host)" /></Field>
      </div>
      <Field label="Closing note" hint="The warm line in the footer.">
        <TextArea value={e.closingNote} onChange={up('closingNote')} />
      </Field>
    </div>
  );
}

/* -------------------------------- toggles -------------------------------- */
export function TogglesEditor({ settings, setSection }) {
  const t = settings.toggles;
  const flip = (k) => (v) => setSection('toggles', { ...t, [k]: v });
  return (
    <div className="admin-card">
      <h3>Sections guests see</h3>
      <p className="muted" style={{ marginTop: 0 }}>Turn each part of the page on or off.</p>
      <Toggle on={t.registry} onChange={flip('registry')} label="Registry & gifts" />
      <Toggle on={t.wishes} onChange={flip('wishes')} label="Well wishes & advice" sub="The guest book form." />
      <Toggle on={t.wishesPublic} onChange={flip('wishesPublic')} label="Show wishes publicly" sub="Display submitted wishes as a wall on the page. Off keeps them private to you." />
      <Toggle on={t.predictions} onChange={flip('predictions')} label="Baby predictions" />
      <Toggle on={t.gallery} onChange={flip('gallery')} label="Photo gallery" sub="Turns on once you add a photo in Content." />
      <Toggle on={t.faq} onChange={flip('faq')} label="FAQ" />
      <Toggle on={t.schedule} onChange={flip('schedule')} label="Day-of schedule" />
      <Toggle on={t.giftTracker} onChange={flip('giftTracker')} label="Gift & thank-you tracker" sub="Private to you. Never shown to guests." />
      <Toggle on={t.virtual} onChange={flip('virtual')} label="Joining from afar (virtual)" sub="Off for an in-person event." />
    </div>
  );
}

/* ------------------------------- registry -------------------------------- */
export function RegistryEditor({ settings, setSection }) {
  const list = settings.registry || [];
  const update = (next) => setSection('registry', next);
  const edit = (i, patch) => update(list.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const add = (type) => update([...list, { id: makeId('reg'), type, label: '', url: '', description: '' }]);
  return (
    <div className="admin-card">
      <h3>Registry & gifts</h3>
      <p className="muted" style={{ marginTop: 0 }}>Link entries become buttons. Note entries are gentle text ideas.</p>
      {list.map((r, i) => (
        <ListRow key={r.id}
          onRemove={() => update(list.filter((_, idx) => idx !== i))}
          onUp={i > 0 ? () => update(reorder(list, i, -1)) : null}
          onDown={i < list.length - 1 ? () => update(reorder(list, i, 1)) : null}>
          <div className="pill" style={{ marginBottom: 10 }}>{r.type === 'link' ? 'Link' : 'Note'}</div>
          <Field label="Label"><Input value={r.label} onChange={(v) => edit(i, { label: v })} placeholder={r.type === 'link' ? 'e.g. Amazon registry' : 'e.g. Bring a book instead of a card'} /></Field>
          {r.type === 'link' && <Field label="URL"><Input type="url" value={r.url} onChange={(v) => edit(i, { url: v })} placeholder="https://" /></Field>}
          <Field label={r.type === 'link' ? 'Short description (optional)' : 'Description'}>
            <TextArea value={r.description} onChange={(v) => edit(i, { description: v })} style={{ minHeight: 64 }} />
          </Field>
        </ListRow>
      ))}
      <div className="row">
        <button className="btn btn-secondary btn-sm" onClick={() => add('link')}>+ Add link</button>
        <button className="btn btn-ghost btn-sm" onClick={() => add('note')}>+ Add note</button>
      </div>
    </div>
  );
}

/* ----------------------------------- FAQ --------------------------------- */
export function FAQEditor({ settings, setSection }) {
  const list = settings.faq || [];
  const update = (next) => setSection('faq', next);
  const edit = (i, patch) => update(list.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  return (
    <div className="admin-card">
      <h3>FAQ</h3>
      {list.map((q, i) => (
        <ListRow key={q.id}
          onRemove={() => update(list.filter((_, idx) => idx !== i))}
          onUp={i > 0 ? () => update(reorder(list, i, -1)) : null}
          onDown={i < list.length - 1 ? () => update(reorder(list, i, 1)) : null}>
          <Field label="Question"><Input value={q.q} onChange={(v) => edit(i, { q: v })} /></Field>
          <Field label="Answer"><TextArea value={q.a} onChange={(v) => edit(i, { a: v })} style={{ minHeight: 64 }} /></Field>
        </ListRow>
      ))}
      <button className="btn btn-secondary btn-sm" onClick={() => update([...list, { id: makeId('faq'), q: '', a: '' }])}>+ Add question</button>
    </div>
  );
}

/* -------------------------------- schedule ------------------------------- */
export function ScheduleEditor({ settings, setSection }) {
  const list = settings.schedule || [];
  const update = (next) => setSection('schedule', next);
  const edit = (i, patch) => update(list.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  return (
    <div className="admin-card">
      <h3>Day-of schedule</h3>
      <p className="muted" style={{ marginTop: 0 }}>Optional. Shown only when the Schedule section is on.</p>
      {list.map((s, i) => (
        <ListRow key={s.id}
          onRemove={() => update(list.filter((_, idx) => idx !== i))}
          onUp={i > 0 ? () => update(reorder(list, i, -1)) : null}
          onDown={i < list.length - 1 ? () => update(reorder(list, i, 1)) : null}>
          <div className="grid-2">
            <Field label="Time"><Input value={s.time} onChange={(v) => edit(i, { time: v })} placeholder="11:00 AM" /></Field>
            <Field label="What is happening"><Input value={s.item} onChange={(v) => edit(i, { item: v })} placeholder="Brunch & mingling" /></Field>
          </div>
        </ListRow>
      ))}
      <button className="btn btn-secondary btn-sm" onClick={() => update([...list, { id: makeId('sch'), time: '', item: '' }])}>+ Add line</button>
    </div>
  );
}

/* --------------------------------- virtual ------------------------------- */
export function VirtualEditor({ settings, setSection }) {
  const v = settings.virtual || {};
  const up = (k) => (val) => setSection('virtual', { ...v, [k]: val });
  return (
    <div className="admin-card">
      <h3>Joining from afar</h3>
      <p className="muted" style={{ marginTop: 0 }}>Optional. Shown only when the virtual section is on.</p>
      <Field label="Video call link"><Input type="url" value={v.videoLink} onChange={up('videoLink')} placeholder="https://" /></Field>
      <Field label="Printable games PDF link"><Input type="url" value={v.gamesPdfLink} onChange={up('gamesPdfLink')} placeholder="https://" /></Field>
    </div>
  );
}

/* ----------------------------- gallery editor ---------------------------- */
// Self-contained: persists straight to the gallery store. Enables the toggle
// when the first photo is added.
export function GalleryEditor({ onFirstPhoto }) {
  const { loading, error, data, reload } = useAsyncData(() => DB.getGalleryItems(), []);
  const [items, setItems] = useState([]);
  const [saved, setSaved] = useState(false);
  useEffect(() => { if (data) setItems(data); }, [data]);

  async function persist(next, wasEmpty) {
    setItems(next);
    try {
      await DB.saveGalleryItems(next);
      if (wasEmpty && next.length > 0 && onFirstPhoto) onFirstPhoto();
      setSaved(true); setTimeout(() => setSaved(false), 1500);
    } catch (e) { console.error(e); }
  }
  const add = () => persist([...items, { id: makeId('img'), url: '', caption: '' }], items.length === 0);
  const edit = (i, patch) => persist(items.map((x, idx) => (idx === i ? { ...x, ...patch } : x)), false);
  const del = (i) => persist(items.filter((_, idx) => idx !== i), false);

  if (loading) return <div className="admin-card"><Loading /></div>;
  if (error) return <div className="admin-card"><ErrorBlock error={error} onRetry={reload} /></div>;

  return (
    <div className="admin-card">
      <h3>Photo gallery {saved && <span className="pill" style={{ marginLeft: 8 }}>Saved</span>}</h3>
      <p className="muted" style={{ marginTop: 0 }}>Add photos by URL. The gallery section turns on automatically once you add one.</p>
      {items.map((it, i) => (
        <ListRow key={it.id} onRemove={() => del(i)}>
          <Field label="Image URL"><Input type="url" value={it.url} onChange={(v) => edit(i, { url: v })} placeholder="https://" /></Field>
          <Field label="Caption (optional)"><Input value={it.caption} onChange={(v) => edit(i, { caption: v })} /></Field>
          {it.url && <img src={it.url} alt="" style={{ maxHeight: 90, borderRadius: 8, marginTop: 4 }} />}
        </ListRow>
      ))}
      <button className="btn btn-secondary btn-sm" onClick={add}>+ Add photo</button>
    </div>
  );
}
